import { Fragment, memo, useEffect, useLayoutEffect, useRef } from "react";
import styled from "styled-components";

const commandSymbols = {
  alpha: "α",
  beta: "β",
  gamma: "γ",
  delta: "δ",
  epsilon: "ϵ",
  varepsilon: "ε",
  zeta: "ζ",
  eta: "η",
  Delta: "Δ",
  theta: "θ",
  vartheta: "ϑ",
  iota: "ι",
  kappa: "κ",
  lambda: "λ",
  Lambda: "Λ",
  mu: "μ",
  nu: "ν",
  xi: "ξ",
  Xi: "Ξ",
  omicron: "ο",
  rho: "ρ",
  varrho: "ϱ",
  pi: "π",
  Pi: "Π",
  varpi: "ϖ",
  sigma: "σ",
  varsigma: "ς",
  Sigma: "Σ",
  tau: "τ",
  upsilon: "υ",
  Upsilon: "Υ",
  phi: "φ",
  varphi: "φ",
  Phi: "Φ",
  chi: "χ",
  psi: "ψ",
  Psi: "Ψ",
  omega: "ω",
  Omega: "Ω",
  aleph: "ℵ",
  hbar: "ℏ",
  ell: "ℓ",
  times: "×",
  div: "÷",
  ast: "*",
  star: "⋆",
  bullet: "•",
  circ: "°",
  bigcirc: "○",
  oplus: "⊕",
  ominus: "⊖",
  otimes: "⊗",
  oslash: "⊘",
  odot: "⊙",
  wedge: "∧",
  vee: "∨",
  cdot: "·",
  cdotp: "·",
  cdots: "⋯",
  dots: "…",
  ldots: "…",
  vdots: "⋮",
  ddots: "⋱",
  colon: ":",
  ldotp: ".",
  pm: "±",
  mp: "∓",
  neq: "≠",
  ne: "≠",
  not: "̸",
  lt: "<",
  gt: ">",
  le: "≤",
  leq: "≤",
  leqslant: "≤",
  nleq: "≰",
  ngeq: "≱",
  ge: "≥",
  geq: "≥",
  geqslant: "≥",
  ll: "≪",
  gg: "≫",
  approx: "≈",
  asymp: "≍",
  simeq: "≈",
  cong: "≅",
  equiv: "≡",
  notequiv: "≢",
  sim: "∼",
  doteq: "≐",
  propto: "∝",
  infty: "∞",
  partial: "∂",
  nabla: "∇",
  imath: "ı",
  jmath: "ȷ",
  sum: "∑",
  prod: "∏",
  coprod: "∐",
  int: "∫",
  iint: "∬",
  iiint: "∭",
  oint: "∮",
  cup: "∪",
  cap: "∩",
  bigcup: "⋃",
  bigcap: "⋂",
  setminus: "∖",
  backslash: "\\",
  mid: "|",
  nmid: "∤",
  subset: "⊂",
  subseteq: "⊆",
  nsubseteq: "⊈",
  supset: "⊃",
  supseteq: "⊇",
  nsupseteq: "⊉",
  sqsubset: "⊏",
  sqsubseteq: "⊑",
  sqsupset: "⊐",
  sqsupseteq: "⊒",
  in: "∈",
  notin: "∉",
  ni: "∋",
  emptyset: "∅",
  varnothing: "∅",
  therefore: "∴",
  because: "∵",
  implies: "⇒",
  impliedby: "⇐",
  iff: "⇔",
  angle: "∠",
  measuredangle: "∡",
  sphericalangle: "∢",
  triangle: "△",
  square: "□",
  diamond: "◇",
  parallel: "⫽",
  nparallel: "∦",
  perp: "⊥",
  degree: "°",
  rightarrow: "→",
  Rightarrow: "⇒",
  to: "→",
  gets: "←",
  leftarrow: "←",
  Leftarrow: "⇐",
  mapsto: "↦",
  leftrightarrow: "↔",
  Leftrightarrow: "⇔",
  longrightarrow: "⟶",
  Longrightarrow: "⟹",
  longleftarrow: "⟵",
  Longleftarrow: "⟸",
  longleftrightarrow: "⟷",
  Longleftrightarrow: "⟺",
  uparrow: "↑",
  Uparrow: "⇑",
  downarrow: "↓",
  Downarrow: "⇓",
  updownarrow: "↕",
  Updownarrow: "⇕",
  hookrightarrow: "↪",
  hookleftarrow: "↩",
  rightharpoonup: "⇀",
  rightharpoondown: "⇁",
  leftharpoonup: "↼",
  leftharpoondown: "↽",
  land: "∧",
  lor: "∨",
  neg: "¬",
  lnot: "¬",
  forall: "∀",
  exists: "∃",
  Re: "ℜ",
  Im: "ℑ",
  top: "⊤",
  bot: "⊥",
  models: "⊨",
  vdash: "⊢",
  dashv: "⊣",
  prime: "′",
  backprime: "‵",
  vert: "|",
  Vert: "‖",
  lvert: "|",
  rvert: "|",
  lVert: "‖",
  rVert: "‖",
  lbrace: "{",
  rbrace: "}",
  langle: "⟨",
  rangle: "⟩",
  lmoustache: "⎰",
  rmoustache: "⎱",
  lceil: "⌈",
  rceil: "⌉",
  lfloor: "⌊",
  rfloor: "⌋",
  backepsilon: "϶",
  complement: "∁",
  "∖": "\\",
  " ": "",
  "\u0026": "&",
  "%": "%",
  "#": "#",
  "_": "_",
  "{": "{",
  "}": "}",
};

const operatorCommands = new Set([
  "arccos",
  "arcsin",
  "arctan",
  "cos",
  "cot",
  "csc",
  "det",
  "deg",
  "dim",
  "exp",
  "gcd",
  "hom",
  "ker",
  "lcm",
  "lim",
  "liminf",
  "limsup",
  "ln",
  "log",
  "max",
  "min",
  "mod",
  "Pr",
  "sec",
  "sin",
  "tan",
]);

const ignoredCommands = new Set([
  "displaystyle",
  "textstyle",
  "scriptstyle",
  "scriptscriptstyle",
  "normalstyle",
  "!",
  ",",
  ";",
  ":",
  "enspace",
  "thinspace",
  "negthinspace",
  "kern",
  "mkern",
  "hline",
  "newline",
  "linebreak",
  "pagebreak",
  "noindent",
  "vspace",
  "smallskip",
  "medskip",
  "bigskip",
  "limits",
  "nolimits",
  "displaystyle",
  "tiny",
  "scriptsize",
  "footnotesize",
  "small",
  "normalsize",
  "large",
  "Large",
  "LARGE",
  "huge",
  "Huge",
]);

const fontCommands = new Set([
  "mathrm",
  "mathbf",
  "mathbb",
  "mathcal",
  "mathfrak",
  "mathit",
  "mathnormal",
  "mathsf",
  "mathtt",
  "textbf",
  "textit",
  "textrm",
  "textsf",
  "texttt",
  "textnormal",
  "emph",
]);

const accentCommands = {
  bar: "¯",
  breve: "˘",
  check: "ˇ",
  dot: "˙",
  ddot: "¨",
  hat: "^",
  overleftarrow: "←",
  overleftrightarrow: "↔",
  overrightarrow: "→",
  tilde: "~",
  vec: "→",
  widehat: "^",
  widetilde: "~",
};

const decorationCommands = new Set([
  "cancel",
  "bcancel",
  "xcancel",
  "sout",
]);

const delimiterSizeCommands = new Set([
  "big",
  "Big",
  "bigg",
  "Bigg",
  "bigl",
  "Bigl",
  "biggl",
  "Biggl",
  "bigr",
  "Bigr",
  "biggr",
  "Biggr",
  "bigm",
  "Bigm",
  "biggm",
  "Biggm",
]);

// Saved previews mount the same document several times (measurement copy and
// visible reader rows). Parsed element trees are immutable, so reuse them.
const renderedSegmentCache = new Map();
const renderedSegmentCacheLimit = 1500;

function renderCachedTextSegments(text) {
  const cached = renderedSegmentCache.get(text);
  if (cached) {
    renderedSegmentCache.delete(text);
    renderedSegmentCache.set(text, cached);
    return cached;
  }
  const rendered = renderTextSegments(text);
  renderedSegmentCache.set(text, rendered);
  if (renderedSegmentCache.size > renderedSegmentCacheLimit) {
    renderedSegmentCache.delete(renderedSegmentCache.keys().next().value);
  }
  return rendered;
}

function MathText({ source, fallback = "無題目" }) {
  const text = getDisplayText(source, fallback);

  return <MathTextRoot>{renderCachedTextSegments(text)}</MathTextRoot>;
}

export default memo(MathText);

function getDisplayText(source, fallback) {
  if (source === null || source === undefined || source === "") {
    return String(fallback ?? "");
  }
  return String(source).replace(/∥/g, "⫽");
}

function renderTextSegments(value) {
  const segments = splitMathSegments(value);

  return segments.flatMap((segment, index) => {
    if (segment.type === "text") {
      let text = segment.value;
      if (segments[index + 1]?.type === "math" && segments[index + 1]?.display) {
        text = text.replace(/[\t \r\n]+$/, "");
      }
      if (segments[index - 1]?.type === "math" && segments[index - 1]?.display) {
        text = text.replace(/^[\t \r\n]+/, "");
      }
      return text ? <Fragment key={`text-${index}`}>{text}</Fragment> : [];
    }

    return (
      <FormulaSegment
        key={`math-${index}`}
        display={segment.display}
        formula={segment.value}
        segmentKey={`math-${index}`}
      />
    );
  });
}

export function splitMathSegments(value) {
  const source = String(value || "").replace(/＄/g, "$");
  const segments = [];
  let index = 0;

  while (index < source.length) {
    const openingDelimiter = findNextOpeningDelimiter(source, index);

    if (!openingDelimiter) {
      pushTextSegment(segments, source.slice(index));
      break;
    }

    if (openingDelimiter.index > index) {
      pushTextSegment(segments, source.slice(index, openingDelimiter.index));
    }

    const formulaStart = openingDelimiter.index + openingDelimiter.open.length;
    const delimiterEnd = findClosingDelimiter(
      source,
      openingDelimiter.close,
      formulaStart,
    );

    if (delimiterEnd === -1) {
      pushTextSegment(segments, source.slice(openingDelimiter.index));
      break;
    }

    segments.push({
      type: "math",
      display: openingDelimiter.display,
      value: source.slice(formulaStart, delimiterEnd),
    });
    index = delimiterEnd + openingDelimiter.close.length;
  }

  return segments;
}

function pushTextSegment(segments, value) {
  if (!value) return;
  const lastSegment = segments[segments.length - 1];
  if (lastSegment?.type === "text") {
    lastSegment.value += value;
    return;
  }
  segments.push({ type: "text", value });
}

function findNextOpeningDelimiter(source, startIndex) {
  for (let index = startIndex; index < source.length; index += 1) {
    if (source[index - 1] === "\\") continue;
    if (source[index] === "$") {
      const display = source.startsWith("$$", index);
      return {
        index,
        open: display ? "$$" : "$",
        close: display ? "$$" : "$",
        display,
      };
    }
  }
  return null;
}

function findClosingDelimiter(source, delimiter, startIndex) {
  let index = startIndex;
  while (index < source.length) {
    const foundIndex = source.indexOf(delimiter, index);
    if (foundIndex === -1) return -1;
    if (source[foundIndex - 1] !== "\\") return foundIndex;
    index = foundIndex + delimiter.length;
  }
  return -1;
}

function FormulaSegment({ display, formula, segmentKey }) {
  const normalizedFormula = normalizeFormula(formula);
  const promotedToDisplay = !display && hasFormulaLineBreak(normalizedFormula);
  const shouldDisplay = display || promotedToDisplay;

  if (shouldDisplay) {
    const lines = splitFormulaLines(normalizedFormula);
    return (
      <MathDisplay
        className={`katex-display math-display${
          promotedToDisplay ? " math-display-left" : ""
        }`}
      >
        {lines.map((line, index) => (
          <MathLine className="katex math-line" key={`${segmentKey}-line-${index}`}>
            {renderFormula(line, `${segmentKey}-line-${index}`)}
          </MathLine>
        ))}
      </MathDisplay>
    );
  }

  return (
    <MathInline className="katex math-inline">
      {renderFormula(normalizedFormula, `${segmentKey}-inline`)}
    </MathInline>
  );
}

const matrixEnvironments = new Set(["matrix", "pmatrix", "bmatrix", "Bmatrix", "vmatrix", "Vmatrix"]);

// Matrix row separators belong to their own table, not the surrounding formula.
function protectMatrixEnvironments(value) {
  const source = String(value || "");
  let prefix = "MATHMATRIXBLOCK";
  while (source.includes(prefix)) prefix += "X";
  const blocks = [];
  const pattern = /\\(begin|end)\s*\{(matrix|pmatrix|bmatrix|Bmatrix|vmatrix|Vmatrix)\}/g;
  let depth = 0;
  let start = 0;
  let previous = 0;
  let protectedSource = "";
  let match;
  while ((match = pattern.exec(source))) {
    if (match[1] === "begin") {
      if (depth === 0) start = match.index;
      depth += 1;
    } else if (depth > 0) {
      depth -= 1;
      if (depth === 0) {
        const marker = `${prefix}${blocks.length}END`;
        blocks.push([marker, source.slice(start, pattern.lastIndex)]);
        protectedSource += source.slice(previous, start) + marker;
        previous = pattern.lastIndex;
      }
    }
  }
  protectedSource += source.slice(previous);
  return {
    source: protectedSource,
    restore(text) {
      return blocks.reduce((result, [marker, block]) => result.split(marker).join(block), text);
    },
  };
}

function hasFormulaLineBreak(value) {
  const protectedMatrices = protectMatrixEnvironments(value);
  return /\\\\(?:\s*\[[^\]]+\])?|\n[ \t]*\\=/.test(protectedMatrices.source);
}

function normalizeFormula(value) {
  const protectedMatrices = protectMatrixEnvironments(value);
  return protectedMatrices.restore(protectedMatrices.source
    .replace(/＄/g, "$")
    .replace(/\/(implies|impliedby|iff)\b/g, "\\$1")
    .replace(/\\begin\{(?:array|matrix|pmatrix|bmatrix|vmatrix|Vmatrix|cases)\}(?:\{[^}]*\})?/g, "")
    .replace(/\\end\{(?:array|matrix|pmatrix|bmatrix|vmatrix|Vmatrix|cases)\}/g, "")
    .replace(/\\begin\{(?:aligned|align\*?|gather\*?|equation\*?|split)\}/g, "")
    .replace(/\\end\{(?:aligned|align\*?|gather\*?|equation\*?|split)\}/g, "")
    .replace(/\\displaystyle/g, "")
    .replace(/\\textstyle/g, "")
    .replace(/\\scriptstyle/g, "")
    .replace(/\\scriptscriptstyle/g, "")
    .replace(/\\[,;:]/g, "")
    .replace(/\\!/g, "")
    .replace(/&/g, "")
    .trim());
}

function splitFormulaLines(value) {
  const protectedMatrices = protectMatrixEnvironments(value);
  const lines = protectedMatrices.source
    .replace(/\r\n/g, "\n")
    .replace(/\n[ \t]*\\=/g, "\\\\=")
    .split(/\\\\(?:\s*\[[^\]]+\])?/)
    .map((line) => line.replace(/\s*\n\s*/g, " ").replace(/[ \t]+/g, " ").trim())
    .filter(Boolean)
    .map((line) => protectedMatrices.restore(line));

  return lines.length ? lines : [value];
}

function splitMatrixRows(source) {
  const rows = [];
  let row = [];
  let cell = "";
  let braces = 0;
  let environmentDepth = 0;
  const finishRow = () => {
    row.push(cell.trim());
    if (row.some((value) => value !== "")) rows.push(row);
    row = [];
    cell = "";
  };
  for (let index = 0; index < source.length;) {
    const rest = source.slice(index);
    const environment = rest.match(/^\\(begin|end)\s*\{[^}]+\}/);
    if (environment) {
      environmentDepth += environment[1] === "begin" ? 1 : -1;
      cell += environment[0];
      index += environment[0].length;
      continue;
    }
    if (braces === 0 && environmentDepth === 0) {
      const separator = rest.match(/^(?:\\\\(?:\s*\[[^\]]+\])?|\\cr\b)/);
      if (separator) { finishRow(); index += separator[0].length; continue; }
      if (source[index] === "&") { row.push(cell.trim()); cell = ""; index += 1; continue; }
    }
    if (source[index] === "\\") {
      cell += source.slice(index, index + 2);
      index += 2;
      continue;
    }
    if (source[index] === "{") braces += 1;
    if (source[index] === "}") braces = Math.max(0, braces - 1);
    cell += source[index++];
  }
  finishRow();
  return rows.length ? rows : [[""]];
}

function renderFormula(formula, keyPrefix) {
  const parser = new LatexParser(formula, keyPrefix);
  return parser.parse();
}

class LatexParser {
  constructor(source, keyPrefix) {
    this.source = source;
    this.index = 0;
    this.keyPrefix = keyPrefix;
    this.sequence = 0;
  }

  parse() {
    return this.parseUntil();
  }

  parseUntil(stopChar, options = {}) {
    const nodes = [];

    while (this.index < this.source.length) {
      if (stopChar && this.source[this.index] === stopChar) {
        this.index += 1;
        break;
      }

      if (options.stopAtRight && this.isCommandAtCurrentIndex("right")) {
        break;
      }

      if (this.source.startsWith("\\\\", this.index)) {
        this.index += 2;
        nodes.push(<br key={this.nextKey("break")} />);
        continue;
      }

      const atom = this.parseAtom(stopChar);
      if (!atom) continue;
      nodes.push(this.applyScripts(atom));
    }

    return nodes;
  }

  parseAtom(stopChar) {
    const char = this.source[this.index];

    if (char === undefined) return null;
    if (char === "}") {
      this.index += 1;
      return null;
    }
    if (char === "{") {
      this.index += 1;
      return (
        <MathGroup key={this.nextKey("group")}>
          {this.parseUntil("}")}
        </MathGroup>
      );
    }
    if (char === "\\") return this.parseCommand();
    if (char === "^" || char === "_") {
      this.index += 1;
      return this.renderText(char);
    }

    return this.parseText(stopChar);
  }

  parseText(stopChar) {
    const start = this.index;
    while (
      this.index < this.source.length &&
      this.source[this.index] !== stopChar &&
      !["\\", "{", "}", "^", "_"].includes(this.source[this.index])
    ) {
      this.index += 1;
    }

    return this.renderText(this.source.slice(start, this.index).replace(/\s|~/g, ""));
  }

  parseCommand() {
    if (this.source.startsWith("\\\\", this.index)) {
      this.index += 2;
      return <br key={this.nextKey("break")} />;
    }

    this.index += 1;
    const command = this.readCommandName();

    if (!command) return this.renderText("\\");

    if (["hspace", "kern", "mkern"].includes(command)) {
      if (this.source[this.index] === "*") this.index += 1;
      this.readRawGroup();
      return null;
    }

    if (command === "rule") {
      const width = parseMathSpaceLength(this.readRawGroup());
      const height = parseMathSpaceLength(this.readRawGroup());
      return (
        <MathRule
          aria-hidden="true"
          key={this.nextKey("rule")}
          style={{ width, height }}
        />
      );
    }

    if (command === "not") {
      const symbol = this.readDelimiter();
      return this.renderText(`${symbol}\u0338`.normalize("NFC"));
    }

    if (ignoredCommands.has(command)) return null;

    if (delimiterSizeCommands.has(command)) {
      const delimiter = this.readDelimiter();
      return delimiter ? (
        <MathDelimiter className="math-delimiter" key={this.nextKey(command)}>
          {delimiter}
        </MathDelimiter>
      ) : null;
    }

    if (["frac", "dfrac", "tfrac"].includes(command)) {
      const numerator = this.parseRequiredGroup();
      const denominator = this.parseRequiredGroup();
      return (
        <MathFraction className="math-frac" $alignRadicand={denominator.length === 1 && denominator[0]?.props?.className === "math-radical" && !denominator[0]?.props?.children?.[0] && !containsTallMathNode(numerator)} key={this.nextKey("frac")}>
          <span>{numerator}</span>
          <span>{denominator}</span>
        </MathFraction>
      );
    }

    if (command === "sqrt") {
      const degree = this.parseOptionalBracketGroup();
      const radicand = this.parseRequiredGroup();
      return (
        <MathRadical className="math-radical" key={this.nextKey("sqrt")}>
          {degree.length ? <MathRootDegree>{degree}</MathRootDegree> : null}
          <MathRadicalSymbol aria-hidden="true"><span>√</span></MathRadicalSymbol>
          <MathRadicand>{radicand}</MathRadicand>
        </MathRadical>
      );
    }

    if (command === "left") {
      const leftDelimiter = this.readDelimiter();
      const content = this.parseUntil(null, { stopAtRight: true });
      const isTall = containsTallMathNode(content);
      let rightDelimiter = "";
      if (this.isCommandAtCurrentIndex("right")) {
        this.index += "\\right".length;
        rightDelimiter = this.readDelimiter();
      }
      return (
        <MathDelimited
          className={`math-delimited${isTall ? " math-delimited-tall" : ""}`}
          key={this.nextKey("delimited")}
        >
          {leftDelimiter ? (
            <MathDelimiter className="math-delimiter">{leftDelimiter}</MathDelimiter>
          ) : null}
          <MathDelimitedContent className="math-delimited-content">{content}</MathDelimitedContent>
          {rightDelimiter ? (
            <MathDelimiter className="math-delimiter">{rightDelimiter}</MathDelimiter>
          ) : null}
        </MathDelimited>
      );
    }

    if (command === "right") {
      this.skipSpaces();
      if (this.source[this.index] === ".") {
        this.index += 1;
        return null;
      }
      return this.renderText(this.readDelimiter());
    }

    if (command === "begin" || command === "end") {
      const environment = this.readRawGroup();
      if (command === "begin" && matrixEnvironments.has(environment)) return this.parseMatrix(environment);
      return null;
    }

    if (["text", "operatorname"].includes(command)) {
      const text = decodeLatexText(this.readRawGroup()).replace(/\s/g, "");
      return (
        <MathTextRun key={this.nextKey("text-command")}>
          {text}
        </MathTextRun>
      );
    }

    if (fontCommands.has(command)) {
      return (
        <MathTextRun key={this.nextKey("font-command")}>
          {this.parseRequiredGroup()}
        </MathTextRun>
      );
    }

    if (command === "color") {
      this.readRawGroup();
      return (
        <MathTextRun key={this.nextKey("color")}>
          {this.parseRequiredGroup()}
        </MathTextRun>
      );
    }

    if (command === "colorbox") {
      this.readRawGroup();
      return (
        <MathBox key={this.nextKey("colorbox")}>
          {this.parseRequiredGroup()}
        </MathBox>
      );
    }

    if (command === "binom" || command === "choose") {
      const numerator = this.parseRequiredGroup();
      const denominator = this.parseRequiredGroup();
      return (
        <MathDelimited className="math-delimited math-delimited-tall" key={this.nextKey("binom")}>
          <MathDelimiter className="math-delimiter">(</MathDelimiter>
          <MathDelimitedContent className="math-delimited-content"><MathBinomial className="math-binom">
            <span>{numerator}</span>
            <span>{denominator}</span>
          </MathBinomial></MathDelimitedContent>
          <MathDelimiter className="math-delimiter">)</MathDelimiter>
        </MathDelimited>
      );
    }

    if (["phantom", "hphantom", "vphantom"].includes(command)) {
      return (
        <MathPhantom aria-hidden="true" key={this.nextKey("phantom")}>
          {this.parseRequiredGroup()}
        </MathPhantom>
      );
    }

    if (command === "overline") {
      return (
        <MathOverline key={this.nextKey("overline")}>
          {this.parseRequiredGroup()}
        </MathOverline>
      );
    }

    if (command === "underline") {
      const content = this.parseRequiredGroup();
      const hasCircledText = content.some((node) =>
        isMathElementWithClass(node, "math-textcircled"),
      );
      return (
        <MathUnderline
          className={`math-underline${hasCircledText ? " math-underline-enclosed" : ""}`}
          key={this.nextKey("underline")}
        >
          {content}
        </MathUnderline>
      );
    }

    if (command === "textcircled") {
      return (
        <MathTextCircled className="math-textcircled" key={this.nextKey("textcircled")}>
          {this.parseRequiredGroup()}
        </MathTextCircled>
      );
    }

    if (command === "overbrace" || command === "underbrace") {
      return (
        <MathBrace
          className={`math-${command}`}
          key={this.nextKey(command)}
          $position={command === "overbrace" ? "over" : "under"}
        >
          {this.parseRequiredGroup()}
        </MathBrace>
      );
    }

    if (Object.prototype.hasOwnProperty.call(accentCommands, command)) {
      return (
        <MathAccent key={this.nextKey("accent")} data-accent={accentCommands[command]}>
          {this.parseRequiredGroup()}
        </MathAccent>
      );
    }

    if (command === "boxed") {
      return (
        <MathBox key={this.nextKey("boxed")}>
          {this.parseRequiredGroup()}
        </MathBox>
      );
    }

    if (command === "fbox") {
      return (
        <MathBox key={this.nextKey("fbox")}>
          {this.parseRequiredGroup()}
        </MathBox>
      );
    }

    if (command === "pmod") {
      return (
        <MathTextRun key={this.nextKey("pmod")}>
          {" (mod "}
          {this.parseRequiredGroup()}
          {")"}
        </MathTextRun>
      );
    }

    if (command === "bmod") {
      return this.renderText(" mod ");
    }

    if (command === "overset" || command === "stackrel") {
      const over = this.parseRequiredGroup();
      const base = this.parseRequiredGroup();
      return (
        <MathStack key={this.nextKey("overset")}>
          <MathStackOver>{over}</MathStackOver>
          <span>{base}</span>
        </MathStack>
      );
    }

    if (command === "underset") {
      const under = this.parseRequiredGroup();
      const base = this.parseRequiredGroup();
      return (
        <MathStack key={this.nextKey("underset")}>
          <span>{base}</span>
          <MathStackUnder>{under}</MathStackUnder>
        </MathStack>
      );
    }

    if (command === "substack") {
      const content = this.parseRequiredGroup();
      return <MathSubstack key={this.nextKey("substack")}>{content}</MathSubstack>;
    }

    if (decorationCommands.has(command)) {
      return (
        <MathCancel className={`math-${command}`} key={this.nextKey(command)}>
          {this.parseRequiredGroup()}
        </MathCancel>
      );
    }

    if (command === "quad" || command === "qquad") return null;

    if (operatorCommands.has(command)) {
      return (
        <MathOperator key={this.nextKey("operator")}>
          {command}
        </MathOperator>
      );
    }

    return this.renderText(commandSymbols[command] ?? command);
  }

  parseMatrix(environment) {
    const start = this.index;
    const pattern = /\\(begin|end)\s*\{(matrix|pmatrix|bmatrix|Bmatrix|vmatrix|Vmatrix)\}/g;
    pattern.lastIndex = start;
    let depth = 1;
    let match;
    let end = this.source.length;
    this.index = this.source.length;
    while ((match = pattern.exec(this.source))) {
      depth += match[1] === "begin" ? 1 : -1;
      if (depth === 0) { end = match.index; this.index = pattern.lastIndex; break; }
    }
    const rows = splitMatrixRows(this.source.slice(start, end));
    const matrixKey = this.nextKey("matrix");
    const matrix = <MathMatrix className="math-matrix" role="table" aria-label="矩陣" key={matrixKey}>
      {rows.map((row, rowIndex) => <span className="math-matrix-row" role="row" key={`${matrixKey}-row-${rowIndex}`}>
        {row.map((cell, cellIndex) => <span className="math-matrix-cell" role="cell" key={`${matrixKey}-cell-${rowIndex}-${cellIndex}`}>
          {renderFormula(normalizeFormula(cell), `${matrixKey}-${rowIndex}-${cellIndex}`)}
        </span>)}
      </span>)}
    </MathMatrix>;
    const delimiters = { pmatrix: ["(", ")"], bmatrix: ["[", "]"], Bmatrix: ["{", "}"], vmatrix: ["|", "|"], Vmatrix: ["‖", "‖"] }[environment];
    if (!delimiters) return matrix;
    return <MathDelimited className="math-delimited math-delimited-tall" key={this.nextKey("matrix-delimited")}>
      <MathDelimiter className="math-delimiter">{delimiters[0]}</MathDelimiter>
      <MathDelimitedContent className="math-delimited-content">{matrix}</MathDelimitedContent>
      <MathDelimiter className="math-delimiter">{delimiters[1]}</MathDelimiter>
    </MathDelimited>;
  }

  readCommandName() {
    const start = this.index;
    if (/[A-Za-z]/.test(this.source[this.index] || "")) {
      while (/[A-Za-z]/.test(this.source[this.index] || "")) {
        this.index += 1;
      }
      return this.source.slice(start, this.index);
    }

    this.index += 1;
    return this.source.slice(start, this.index);
  }

  isCommandAtCurrentIndex(command) {
    if (!this.source.startsWith(`\\${command}`, this.index)) return false;
    const nextChar = this.source[this.index + command.length + 1] || "";
    return !/[A-Za-z]/.test(nextChar);
  }

  readDelimiter() {
    this.skipSpaces();
    if (this.source[this.index] === ".") {
      this.index += 1;
      return "";
    }

    if (this.source[this.index] === "\\") {
      this.index += 1;
      const command = this.readCommandName();
      return commandSymbols[command] || command || "\\";
    }

    const delimiter = this.source[this.index] || "";
    if (delimiter) this.index += 1;
    return delimiter;
  }

  parseRequiredGroup() {
    this.skipSpaces();
    if (this.source[this.index] === "{") {
      this.index += 1;
      return this.parseUntil("}");
    }

    // LaTeX allows a required argument to be either a braced group or one
    // unbraced token.  In particular, `\frac12` means `\frac{1}{2}`, not
    // `\frac{12}{}`.  parseAtom() consumes a whole run of text, so read only
    // one character for the unbraced form.
    let atom;
    if (this.source[this.index] === "\\") {
      atom = this.parseCommand();
    } else if (this.index < this.source.length) {
      atom = this.renderText(this.source[this.index]);
      this.index += 1;
    } else {
      atom = null;
    }
    return atom ? [this.applyScripts(atom)] : [];
  }

  parseOptionalBracketGroup() {
    this.skipSpaces();
    if (this.source[this.index] !== "[") return [];
    this.index += 1;
    return this.parseUntil("]");
  }

  readRawGroup() {
    this.skipSpaces();
    if (this.source[this.index] !== "{") return "";

    this.index += 1;
    const start = this.index;
    let depth = 1;

    while (this.index < this.source.length && depth > 0) {
      const char = this.source[this.index];
      if (char === "{") depth += 1;
      if (char === "}") depth -= 1;
      this.index += 1;
    }

    return this.source.slice(start, Math.max(start, this.index - 1));
  }

  applyScripts(base) {
    let superscript = null;
    let subscript = null;

    while (this.source[this.index] === "^" || this.source[this.index] === "_") {
      const scriptType = this.source[this.index];
      this.index += 1;
      const script = this.parseScriptArgument(scriptType);
      if (scriptType === "^") superscript = script;
      if (scriptType === "_") subscript = script;
    }

    if (!superscript && !subscript) return base;

    if (isMathElementWithClass(base, "math-underbrace") && subscript && !superscript) {
      return (
        <MathBraceScript className="math-brace-script math-brace-script-under" key={this.nextKey("brace-script")}>
          <MathBraceBase>{base}</MathBraceBase>
          <MathBraceLabel>{subscript}</MathBraceLabel>
        </MathBraceScript>
      );
    }

    if (isMathElementWithClass(base, "math-overbrace") && superscript && !subscript) {
      return (
        <MathBraceScript className="math-brace-script math-brace-script-over" key={this.nextKey("brace-script")}>
          <MathBraceLabel>{superscript}</MathBraceLabel>
          <MathBraceBase>{base}</MathBraceBase>
        </MathBraceScript>
      );
    }

    return (
      <MathScript
        className={`math-script${
          superscript && subscript
            ? " math-script-both"
            : superscript
              ? " math-script-sup"
              : " math-script-sub"
        }`}
        key={this.nextKey("script")}
      >
        <MathScriptBase>{base}</MathScriptBase>
        {superscript ? <MathSup>{superscript}</MathSup> : <MathScriptSpacer />}
        {subscript ? <MathSub>{subscript}</MathSub> : <MathScriptSpacer />}
      </MathScript>
    );
  }

  parseScriptArgument(scriptType) {
    this.skipSpaces();
    if (this.source[this.index] === "{") {
      this.index += 1;
      return this.parseUntil("}");
    }

    if (this.source[this.index] === "\\") {
      const atom = this.parseCommand();
      return atom ? [atom] : [];
    }

    const char = this.source[this.index];
    if (char === undefined) return [];
    this.index += 1;
    const atom = this.renderText(char);
    if (!atom) return [];

    const choiceSuffix =
      scriptType === "^" && /[\dA-Za-z]/.test(char)
        ? this.readScriptChoiceSuffix()
        : "";
    if (!choiceSuffix) return [atom];

    return [
      atom,
      <MathTextRun key={this.nextKey("script-choice")}>
        {choiceSuffix}
      </MathTextRun>,
    ];
  }

  readScriptChoiceSuffix() {
    let index = this.index;
    let suffix = "";

    while (index < this.source.length) {
      const choiceMatch = this.source.slice(index).match(/^\s+(?:or|或)\s+/i);
      if (!choiceMatch) break;

      let tokenIndex = index + choiceMatch[0].length;
      const tokenStart = tokenIndex;
      while (/[\dA-Za-z.]/.test(this.source[tokenIndex] || "")) {
        tokenIndex += 1;
      }
      if (tokenIndex === tokenStart) break;

      suffix += `${choiceMatch[0].replace(/\s+/g, " ")}${this.source.slice(
        tokenStart,
        tokenIndex,
      )}`;
      index = tokenIndex;
    }

    if (suffix) this.index = index;
    return suffix;
  }

  skipSpaces() {
    while (/\s/.test(this.source[this.index] || "")) {
      this.index += 1;
    }
  }

  renderText(value) {
    return <Fragment key={this.nextKey("text")}>{value}</Fragment>;
  }

  nextKey(label) {
    const key = `${this.keyPrefix}-${label}-${this.sequence}`;
    this.sequence += 1;
    return key;
  }
}

function isMathElementWithClass(node, className) {
  return String(node?.props?.className || "")
    .split(/\s+/)
    .includes(className);
}

function decodeLatexText(value) {
  return String(value || "")
    .replace(/\\_/g, "_")
    .replace(/\\%/g, "%")
    .replace(/\\#/g, "#")
    .replace(/\\&/g, "&")
    .replace(/\\\{/g, "{")
    .replace(/\\\}/g, "}")
    .replace(/\\ /g, " ");
}

function parseMathSpaceLength(value) {
  const normalized = String(value || "").trim();
  const match = normalized.match(/^([+-]?(?:\d+\.?\d*|\.\d+))\s*(em|ex|cm|mm|in|pt|pc|px)?$/i);
  if (!match) return "1em";

  const amount = Math.max(0, Math.min(Number(match[1]), 12));
  const unit = (match[2] || "em").toLowerCase();
  return `${amount}${unit}`;
}

function containsTallMathNode(nodes) {
  return flattenReactNodes(nodes).some((node) => {
    const className = String(node?.props?.className || "");
    if (/\b(?:math-frac|math-radical|math-matrix|math-delimited-tall)\b/.test(className)) {
      return true;
    }
    return containsTallMathNode(node?.props?.children);
  });
}

function flattenReactNodes(nodes) {
  if (nodes === null || nodes === undefined || typeof nodes === "boolean") return [];
  return Array.isArray(nodes) ? nodes.flatMap(flattenReactNodes) : [nodes];
}

const MathTextRoot = styled.span`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;

  .math-inline,
  .math-display {
    color: inherit;
    font-family: var(--handout-math-font, "Cambria Math", "STIX Two Math", "Times New Roman", serif);
  }
`;

const MathInline = styled.span`
  display: inline;
  line-height: 1.25;
  vertical-align: baseline;
`;

const MathDisplay = styled.span`
  max-width: 100%;
  display: block;
  margin: 7px 0;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 4px 0;
  text-align: inherit;

  &.math-display-left {
    width: fit-content !important;
    max-width: 100% !important;
    margin-left: 0 !important;
    margin-right: auto !important;
    text-align: left !important;
  }

  &.math-display-left .math-line {
    text-align: left !important;
    width: fit-content !important;
    max-width: 100% !important;
    justify-content: flex-start !important;
    margin-left: 0 !important;
    margin-right: auto !important;
  }
`;

const MathLine = styled.span`
  width: fit-content;
  max-width: 100%;
  min-width: max-content;
  display: block;
  text-align: center;
  margin-left: auto;
  margin-right: auto;
  white-space: nowrap;

  & + & {
    margin-top: 4px;
  }
`;

const MathGroup = styled.span`
  display: inline;
`;

const MathTextRun = styled.span`
  font-family: inherit;
`;

const MathRule = styled.span`
  display: inline-block;
  min-width: 0.2em;
  min-height: 1px;
  background: currentColor;
  vertical-align: -0.08em;
`;

const MathOperator = styled.span`
  margin: 0 0.12em;
  font-family: inherit;
`;

function MathDelimited({ children, className }) {
  const wrapperRef = useRef(null);
  useBrowserLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const content = Array.from(wrapper?.children || []).find((child) => child.classList.contains("math-delimited-content"));
    if (!content) return undefined;
    const delimiters = Array.from(wrapper.children).filter((child) => child.classList.contains("math-delimiter"));
    const measureBorderBox = (element) => {
      const style = window.getComputedStyle(element);
      const height = parseFloat(style.height);
      const edges = style.boxSizing === "border-box" ? 0
        : [style.paddingTop, style.paddingBottom, style.borderTopWidth, style.borderBottomWidth]
          .reduce((sum, value) => sum + (parseFloat(value) || 0), 0);
      return (Number.isFinite(height) ? height + edges : element.offsetHeight) || 0;
    };
    const stretch = (contentHeight) => {
      if (!(contentHeight > 0)) return;
      const contentRect = content.getBoundingClientRect();
      const coordinateScale = contentRect.height > 0 ? contentRect.height / contentHeight : 1;
      delimiters.forEach((delimiter) => {
        // Computed/offset height excludes transform, so repeated reports cannot feed back.
        const naturalHeight = measureBorderBox(delimiter);
        if (!(naturalHeight > 0)) return;
        const glyphRect = delimiter.getBoundingClientRect();
        const centre = (glyphRect.top + glyphRect.bottom) / 2;
        const reach = contentRect.height > 0 && glyphRect.height > 0
          ? 2 * Math.max(Math.abs(centre - contentRect.top), Math.abs(contentRect.bottom - centre)) / coordinateScale
          : contentHeight;
        const scale = Math.max(1, contentHeight / naturalHeight, reach / naturalHeight);
        const value = String(Math.round(scale * 1000000) / 1000000);
        if (delimiter.style.getPropertyValue("--math-delimiter-scale") !== value) {
          delimiter.style.setProperty("--math-delimiter-scale", value);
        }
      });
    };
    // ResizeObserver reports the first size after layout and before paint.
    // Measuring synchronously here forces one full layout per delimiter.
    if (typeof ResizeObserver === "undefined") {
      stretch(measureBorderBox(content));
      return undefined;
    }
    const observer = new ResizeObserver(([entry]) => {
      const box = entry.borderBoxSize?.[0] || entry.borderBoxSize;
      if (box?.blockSize) stretch(box.blockSize);
      else if (entry.contentRect) {
        const style = window.getComputedStyle(content);
        const edges = [style.paddingTop, style.paddingBottom, style.borderTopWidth, style.borderBottomWidth]
          .reduce((sum, value) => sum + (parseFloat(value) || 0), 0);
        stretch(entry.contentRect.height + edges);
      }
    });
    try { observer.observe(content, { box: "border-box" }); }
    catch { observer.observe(content); }
    return () => observer.disconnect();
  }, []);
  return <MathDelimitedBox ref={wrapperRef} className={className}>{children}</MathDelimitedBox>;
}

const MathDelimitedBox = styled.span`
  display: inline-block;
  vertical-align: baseline;
  white-space: nowrap;
`;

const MathDelimitedContent = styled.span`
  display: inline-block;
  vertical-align: baseline;
`;

const MathDelimiter = styled.span`
  display: inline-block;
  font-size: 1.12em;
  line-height: 1;
  vertical-align: middle;
  transform: scaleY(var(--math-delimiter-scale, 1));
  transform-origin: center;
`;

const useBrowserLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

function MathFraction({ children, className, $alignRadicand }) {
  const fractionRef = useRef(null);
  useBrowserLayoutEffect(() => {
    const fraction = fractionRef.current;
    const denominator = fraction?.lastElementChild;
    if (!denominator || typeof ResizeObserver === "undefined") return undefined;
    let previousHeight = null;
    const align = (height) => {
      if (height > 0 && (previousHeight === null || Math.abs(height - previousHeight) > 0.05)) {
        previousHeight = height;
        fraction.style.setProperty("--math-fraction-align", `calc(0.25em - ${height}px - 0.5px)`);
      }
    };
    // The observer's first report aligns the fraction before paint. A
    // synchronous read here would force a full layout for every fraction.
    const observer = new ResizeObserver(([entry]) => {
      const box = entry.borderBoxSize?.[0] || entry.borderBoxSize;
      if (box?.blockSize) align(box.blockSize);
      else if (entry.contentRect) {
        const current = window.getComputedStyle(denominator);
        const edges = [current.paddingTop, current.paddingBottom, current.borderTopWidth, current.borderBottomWidth]
          .reduce((sum, value) => sum + (parseFloat(value) || 0), 0);
        align(entry.contentRect.height + edges);
      }
    });
    try { observer.observe(denominator, { box: "border-box" }); }
    catch { observer.observe(denominator); }
    return () => observer.disconnect();
  }, []);
  return <MathFractionBox ref={fractionRef} className={className} $alignRadicand={$alignRadicand}>{children}</MathFractionBox>;
}

const MathFractionBox = styled.span`
  display: inline-block;
  overflow: hidden;
  vertical-align: var(--math-fraction-align, middle);
  margin: 0 0.12em;
  line-height: 1;

  > span {
    min-width: 1.1em;
    display: block;
    padding: 0 0.2em;
    text-align: center;
    white-space: nowrap;
  }

  > span:first-child {
    margin-left: ${({ $alignRadicand }) => $alignRadicand ? "0.59em" : "0"};
    border-bottom: 1px solid currentColor;
    padding-top: 0.12em;
    padding-bottom: 0.12em;
  }

  > span:last-child {
    padding-top: 0.12em;
    padding-bottom: 0.12em;
  }

  /* Match TeX script-style sizing so deep continued fractions stay compact. */
  & & {
    margin-left: 0.07em;
    margin-right: 0.07em;
    font-size: 0.9em;
  }

  & & > span {
    min-width: 1em;
    padding-left: 0.14em;
    padding-right: 0.14em;
  }
`;

const MathBinomial = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1;
  vertical-align: middle;

  > span {
    min-width: 1.1em;
    display: inline-block;
    padding: 0.02em 0.14em;
    text-align: center;
    white-space: nowrap;
  }
`;

const MathPhantom = styled.span`
  display: inline-block;
  visibility: hidden;
  white-space: pre;
`;

const MathRadical = styled.span`
  position: relative;
  display: inline-grid;
  grid-template-columns: max-content 0.45em auto;
  align-items: baseline;
  vertical-align: baseline;
  white-space: nowrap;
  margin: 0 0.12em 0 0.3em;
`;

const MathRootDegree = styled.span`
  grid-column: 1;
  grid-row: 1;
  align-self: start;
  margin-right: -0.12em;
  margin-top: 0.1em;
  font-size: 0.62em;
  line-height: 1;
`;

const MathRadicalSymbol = styled.span`
  grid-column: 2;
  grid-row: 1;
  display: block;
  align-self: stretch;
  min-height: 1em;
  width: 0.65em;
  > span { display: none; }
  background: currentColor;
  clip-path: polygon(0 52%, 17% 43%, 38% 83%, 63% 0, 100% 0, 100% 1px, 67% 1px, 40% 100%, 14% 50%, 0 58%);
`;

const MathRadicand = styled.span`
  grid-column: 3;
  grid-row: 1;
  align-self: baseline;
  display: inline-block;
  border-top: 1px solid currentColor;
  padding: 0.04em 0.08em 0 0.04em;
`;

const MathScript = styled.span`
  display: inline-block;
  vertical-align: baseline;
  margin-right: 0.02em;

  &.math-script-both {
    display: inline-grid;
    grid-template-columns: auto auto;
    grid-template-rows: auto auto;
    align-items: center;
    vertical-align: baseline;
    margin-right: 0.04em;
  }
`;

const MathScriptBase = styled.span`
  .math-script-both & {
    grid-row: 1 / 3;
  }
`;

const MathSup = styled.sup`
  position: static;
  top: auto;
  bottom: auto;
  font-size: 0.68em;
  line-height: 1;
  vertical-align: super;

  .math-script-both & {
    grid-column: 2;
    grid-row: 1;
    align-self: end;
  }
`;

const MathSub = styled.sub`
  position: static;
  top: auto;
  bottom: auto;
  font-size: 0.68em;
  line-height: 1;
  vertical-align: sub;

  .math-script-both & {
    grid-column: 2;
    grid-row: 2;
    align-self: start;
  }
`;

const MathScriptSpacer = styled.span`
  display: none;
`;

const MathBraceScript = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  line-height: 1;
  margin: 0 0.08em;
`;

const MathBraceBase = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const MathBraceLabel = styled.span`
  display: block;
  margin-top: 0.08em;
  font-size: 0.68em;
  line-height: 1;
  text-align: center;

  .math-brace-script-over > & {
    margin-top: 0;
    margin-bottom: 0.08em;
  }
`;

const MathOverline = styled.span`
  border-top: 1px solid currentColor;
  padding-top: 0.04em;
`;

const MathUnderline = styled.span`
  border-bottom: 1px solid currentColor;
  padding-bottom: 0.04em;

  &.math-underline-enclosed {
    display: inline-block;
    padding-bottom: 0.16em;
  }
`;

const MathTextCircled = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.4em;
  min-height: 1.4em;
  box-sizing: border-box;
  border: 1px solid currentColor;
  border-radius: 50%;
  padding: 0.08em 0.22em;
  line-height: 1;
  white-space: nowrap;
`;

const MathBrace = styled.span`
  display: inline-flex;
  flex-direction: ${({ $position }) =>
    $position === "under" ? "column" : "column-reverse"};
  align-items: center;
  line-height: 1;

  &::after {
    content: ${({ $position }) => ($position === "under" ? '"⏟"' : '"⏞"')};
    display: block;
    font-size: 1.35em;
    line-height: 0.75;
  }
`;

const MathAccent = styled.span`
  position: relative;
  display: inline-block;
  padding-top: 0.28em;

  &::before {
    content: attr(data-accent);
    position: absolute;
    top: -0.18em;
    left: 0;
    right: 0;
    font-size: 0.85em;
    line-height: 1;
    text-align: center;
  }
`;

const MathBox = styled.span`
  display: inline-block;
  border: 1px solid currentColor;
  border-radius: 4px;
  padding: 0.06em 0.22em;
`;

const MathStack = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  line-height: 1;
  margin: 0 0.08em;
`;

const MathStackOver = styled.span`
  font-size: 0.68em;
  line-height: 1;
  margin-bottom: 0.04em;
`;

const MathStackUnder = styled.span`
  font-size: 0.68em;
  line-height: 1;
  margin-top: 0.04em;
`;

const MathSubstack = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  font-size: 0.78em;
  line-height: 1.1;
`;

const MathCancel = styled.span`
  position: relative;
  display: inline-block;

  &::after {
    content: "";
    position: absolute;
    left: -0.08em;
    right: -0.08em;
    top: 50%;
    border-top: 1px solid currentColor;
    transform: rotate(-16deg);
    transform-origin: center;
  }

  &.math-bcancel::after {
    transform: rotate(16deg);
  }

  &.math-xcancel::before {
    content: "";
    position: absolute;
    left: -0.08em;
    right: -0.08em;
    top: 50%;
    border-top: 1px solid currentColor;
    transform: rotate(16deg);
    transform-origin: center;
  }
`;

const MathMatrix = styled.span`
  display: inline-table;
  border-collapse: separate;
  border-spacing: 0.35em 0.12em;
  vertical-align: middle;
  line-height: 1.15;
  white-space: nowrap;

  > .math-matrix-row { display: table-row; }
  > .math-matrix-row > .math-matrix-cell {
    display: table-cell;
    padding: 0.04em 0.12em;
    text-align: center;
    vertical-align: middle;
  }
`;
